import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, graphBuilderApi } from '../../services/api';
import './GraphPrompts.css';

const PROMPT_GROUPS = [
  {
    label: 'Graph Building',
    keys: [
      'graph_extraction',
      'qa_generation',
      'graph_judge',
      'refine_and_ground',
      'orphan_repair',
      'gate_repair',
      'recap_exclusion',
      'reextraction',
      'entity_resolution',
      'recap_generation',
      'doc_restructure',
    ],
  },
  {
    label: 'Document Preprocessing',
    keys: [
      'chunk_audit',
      'chunk_context',
      'chunk_summarization',
    ],
  },
  {
    label: 'Semantic Analysis',
    keys: [
      'coreference_resolution',
      'relationship_discovery',
      'batch_relationship_discovery',
      'theme_labeling',
      'theme_relationship',
    ],
  },
  {
    label: 'Atomic & Evidence Text',
    keys: [
      'atomic_text',
      'evidence_text',
    ],
  },
  {
    label: 'Compiler',
    keys: [
      'compiler_phase1',
      'compiler_phase2',
      'compiler_phase3',
      'compiler_customization',
    ],
  },
  {
    label: 'Query Processing',
    keys: [
      'query_classifier',
      'query_decomposition',
      'query_expansion',
      'query_reformulation',
    ],
  },
  {
    label: 'Answer Generation',
    keys: [
      'answer_rendering',
      'context_distillation',
      'judge_phase9',
      'conversation_handler',
      'pipeline_adapter',
    ],
  },
  {
    label: 'Explainability',
    keys: [
      'explainability_narrator',
    ],
  },
  {
    label: 'Domain & Vocabulary',
    keys: [
      'domain_vocabulary_extraction',
    ],
  },
  {
    label: 'Meta-Prompts',
    keys: [
      'meta_prompt_generation',
    ],
  },
];

const PROMPT_KEYS = {};
const ALL_KEYS_ORDERED = [];
PROMPT_GROUPS.forEach(g => g.keys.forEach(k => ALL_KEYS_ORDERED.push(k)));

const PROMPT_DISPLAY_NAMES = {
  graph_extraction: 'Graph Extraction',
  qa_generation: 'Q/A Generation',
  graph_judge: 'Graph Quality Judge (Round 2)',
  refine_and_ground: 'Refinement & Grounding',
  orphan_repair: 'Orphan Node Repair',
  gate_repair: 'Gate Repair',
  recap_exclusion: 'Recap Exclusion Instruction',
  reextraction: 'Re-extraction',
  entity_resolution: 'Entity Resolution',
  answer_rendering: 'Answer Rendering (Phase 8)',
  meta_prompt_generation: 'Meta Prompt Generation',
  recap_generation: 'Recap Generation',
  doc_restructure: 'Document Restructuring',
  chunk_audit: 'Chunk Audit (Document Validation)',
  chunk_context: 'Chunk Context Extraction',
  chunk_summarization: 'Chunk Summarization',
  coreference_resolution: 'Coreference Resolution',
  relationship_discovery: 'Cross-Chunk Relationship Discovery',
  batch_relationship_discovery: 'Batch Relationship Discovery',
  theme_labeling: 'Theme Labeling',
  theme_relationship: 'Theme Relationship Discovery',
  atomic_text: 'Atomic Text Generation',
  evidence_text: 'Evidence Text Generation',
  compiler_phase1: 'Compiler Phase 1 - Atom Extraction',
  compiler_phase2: 'Compiler Phase 2 - Platform Wiring',
  compiler_phase3: 'Compiler Phase 3 - Ontology Enrichment',
  compiler_customization: 'Compiler Customization Meta-Prompt',
  query_classifier: 'Query Classifier',
  query_decomposition: 'Query Decomposition',
  query_expansion: 'Query Expansion',
  query_reformulation: 'Query Reformulation',
  context_distillation: 'Context Distillation (Phase 8a)',
  judge_phase9: 'Answer Judge (Phase 9)',
  conversation_handler: 'Conversation Handler',
  pipeline_adapter: 'Pipeline Adapter (Explainability)',
  explainability_narrator: 'Explainability Narrator',
  domain_vocabulary_extraction: 'Domain Vocabulary Extraction',
};
Object.entries(PROMPT_DISPLAY_NAMES).forEach(([k, v]) => { PROMPT_KEYS[k] = v; });

const PROMPT_DESCRIPTIONS = {
  graph_extraction: 'Main prompt for extracting nodes, connections, and gates from text',
  qa_generation: 'Prompt for generating Q/A test pairs to validate graph quality',
  graph_judge: 'Prompt for judging initial graph quality and identifying issues in Round 2 of graph building',
  refine_and_ground: 'Prompt for refining graph based on Q/A validation results',
  orphan_repair: 'Prompt for connecting orphan nodes by adding missing connections',
  gate_repair: 'Prompt for repairing gate structures that have invalid or missing input connections',
  recap_exclusion: 'Instruction appended to the recap prompt telling the LLM to exclude certain content patterns from the recap',
  reextraction: 'Prompt for extracting additional content from uncovered text segments',
  entity_resolution: 'Prompt for identifying and merging duplicate entities across chunks',
  answer_rendering: 'Prompt used in Phase 8 to generate the final answer from Platform results. Variables: {question_section}, {decision}, {confidence}, {evidence}, {explanation}',
  meta_prompt_generation: 'Master template used by AI to generate per-document custom graph extraction prompts. Variables: {document_text_sample}, {graph_description}, {default_graph_extraction_prompt}',
  recap_generation: 'Prompt used to generate the recap/context summary injected before each chunk during graph building',
  doc_restructure: 'Prompt used by Document Converter to reorganize source documents into a graph-builder-friendly format',
  chunk_audit: 'Prompt for LLM-based chunk quality audit during document validation',
  chunk_context: 'Prompt for extracting contextual information from each chunk to improve relationship discovery',
  chunk_summarization: 'Prompt for summarizing individual chunks during recap generation',
  coreference_resolution: 'Prompt for resolving pronoun and entity references within and across chunks',
  relationship_discovery: 'Prompt for discovering semantic relationships between concepts across different chunks',
  batch_relationship_discovery: 'Prompt for batch processing of cross-chunk relationship discovery',
  theme_labeling: 'Prompt for identifying and labeling thematic categories across the document',
  theme_relationship: 'Prompt for discovering relationships between identified themes',
  atomic_text: 'Prompt for generating atomic (self-contained) text representations of graph nodes',
  evidence_text: 'Prompt for generating evidence text that links graph assertions back to source content',
  compiler_phase1: 'Pass 1 of the 3-pass compiler: extracts conditions, outcomes, definitions, entities into nodes/connections/gates',
  compiler_phase2: 'Pass 2 of the 3-pass compiler: Platform wiring, gate canonicalization, deduplication',
  compiler_phase3: 'Pass 3 of the 3-pass compiler: enriches graph with category/group nodes, membership links, aliases',
  compiler_customization: 'Meta-prompt that generates customized Phase 1 and Phase 3 prompts from a document excerpt',
  query_classifier: 'Prompt for classifying whether a user query requires the Platform graph or is purely conversational',
  query_decomposition: 'Prompt for decomposing compound user queries into atomic sub-queries for better semantic matching',
  query_expansion: 'Prompt for expanding user queries with domain vocabulary from the knowledge graph',
  query_reformulation: 'Prompt for reformulating a user query into a clear, unambiguous sentence',
  context_distillation: 'Prompt for distilling relevant context from evidence before answer generation (Phase 8a)',
  judge_phase9: 'Prompt for the judge LLM that evaluates and refines the draft answer in Phase 9',
  conversation_handler: 'System prompt for generating conversational responses grounded in prior Platform context',
  pipeline_adapter: 'System prompt for the deterministic explanation layer that narrates Platform execution results',
  explainability_narrator: 'System prompt for generating plain-English narrations of AI system decisions',
  domain_vocabulary_extraction: 'Per-chunk prompt that extracts domain keywords, context categories, and related domains for MLCF filtering. Runs during graph building (Phase 0.5). Variable: {chunk_text}',
};

const GraphPromptsModal = ({ 
  isOpen, 
  onClose, 
  graphId, 
  documents = [],
  onPromptsUpdated 
}) => {
  const [activeTab, setActiveTab] = useState('graph');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedPromptKey, setSelectedPromptKey] = useState('graph_extraction');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAccountDefault, setSavingAccountDefault] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [defaultPrompts, setDefaultPrompts] = useState({});
  const [graphPrompts, setGraphPrompts] = useState({});
  const [documentPrompts, setDocumentPrompts] = useState({});

  const customizedKeys = useMemo(() => Object.keys(graphPrompts), [graphPrompts]);
  const documentCustomizedKeys = useMemo(() => Object.keys(documentPrompts), [documentPrompts]);
  
  const [editorContent, setEditorContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [showDefault, setShowDefault] = useState(false);

  const loadDefaultPrompts = useCallback(async () => {
    try {
      const result = await graphBuilderApi.getDefaultPrompts();
      setDefaultPrompts(result.prompts || {});
    } catch (err) {
      console.error('Error loading default prompts:', err);
      setError('Failed to load default prompts');
    }
  }, []);

  const loadGraphPrompts = useCallback(async () => {
    if (!graphId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.getGraphPrompts(graphId);
      if (result.success) {
        const custom = {};
        Object.entries(result.prompts || {}).forEach(([key, data]) => {
          if (data.is_customized) {
            custom[key] = data.content;
          }
        });
        setGraphPrompts(custom);
        setDefaultPrompts(result.defaults || {});
      }
    } catch (err) {
      console.error('Error loading graph prompts:', err);
      setError('Failed to load graph prompts');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  const loadDocumentPrompts = useCallback(async () => {
    if (!selectedDocument) {
      setDocumentPrompts({});
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.getDocumentPrompts(selectedDocument);
      if (result.success) {
        const docCustom = {};
        Object.entries(result.prompts || {}).forEach(([key, data]) => {
          if (data.has_document_override) {
            docCustom[key] = data.content;
          }
        });
        setDocumentPrompts(docCustom);
      }
    } catch (err) {
      console.error('Error loading document prompts:', err);
      setError('Failed to load document prompts');
    } finally {
      setLoading(false);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (!isOpen) return;
    
    let content = '';
    if (activeTab === 'graph') {
      content = graphPrompts[selectedPromptKey] || defaultPrompts[selectedPromptKey] || '';
    } else {
      content = documentPrompts[selectedPromptKey] 
        || graphPrompts[selectedPromptKey] 
        || defaultPrompts[selectedPromptKey] 
        || '';
    }
    setEditorContent(content);
    setHasChanges(false);
  }, [isOpen, activeTab, selectedPromptKey, graphPrompts, documentPrompts, defaultPrompts]);

  useEffect(() => {
    if (isOpen) {
      loadDefaultPrompts();
      loadGraphPrompts();
    }
  }, [isOpen, loadDefaultPrompts, loadGraphPrompts]);

  useEffect(() => {
    if (activeTab === 'document' && selectedDocument) {
      loadDocumentPrompts();
    }
  }, [activeTab, selectedDocument, loadDocumentPrompts]);

  const handleEditorChange = (e) => {
    setEditorContent(e.target.value);
    setHasChanges(true);
    setSuccess(null);
  };

  const getCurrentEffectivePrompt = () => {
    if (activeTab === 'graph') {
      return graphPrompts[selectedPromptKey] || defaultPrompts[selectedPromptKey] || '';
    } else {
      return documentPrompts[selectedPromptKey] 
        || graphPrompts[selectedPromptKey] 
        || defaultPrompts[selectedPromptKey] 
        || '';
    }
  };

  const isCurrentPromptCustomized = () => {
    if (activeTab === 'graph') {
      return customizedKeys.includes(selectedPromptKey);
    } else {
      return documentCustomizedKeys.includes(selectedPromptKey);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const prompts = { [selectedPromptKey]: editorContent };
      
      if (activeTab === 'graph') {
        const result = await apiService.updateGraphPrompts(graphId, prompts);
        if (result.success) {
          setGraphPrompts(prev => {
            const next = { ...prev, [selectedPromptKey]: editorContent };
            if (result.customized_keys) {
              const authoritative = new Set(result.customized_keys);
              Object.keys(next).forEach(k => {
                if (!authoritative.has(k)) delete next[k];
              });
            }
            return next;
          });
          setSuccess('Graph prompt saved successfully');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      } else {
        if (!selectedDocument) {
          setError('Please select a document');
          return;
        }
        const result = await apiService.updateDocumentPrompts(selectedDocument, prompts);
        if (result.success) {
          setDocumentPrompts(prev => {
            const next = { ...prev, [selectedPromptKey]: editorContent };
            if (result.customized_keys) {
              const authoritative = new Set(result.customized_keys);
              Object.keys(next).forEach(k => {
                if (!authoritative.has(k)) delete next[k];
              });
            }
            return next;
          });
          setSuccess('Document prompt saved successfully');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      }
    } catch (err) {
      console.error('Error saving prompt:', err);
      setError(err.response?.data?.message || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsAccountDefault = async () => {
    if (!window.confirm(
      'Save this prompt as your account default?\n\n' +
      'All NEW graphs you create will start with this prompt. ' +
      'Existing graphs are not affected.'
    )) return;

    setSavingAccountDefault(true);
    setError(null);
    setSuccess(null);

    try {
      const contentToSave = hasChanges ? editorContent : getCurrentEffectivePrompt();
      const result = await apiService.updateAccountPrompts({
        [selectedPromptKey]: contentToSave,
      });
      if (result.success) {
        setSuccess(`Saved as account default for "${PROMPT_DISPLAY_NAMES[selectedPromptKey]}". New graphs will use this prompt.`);
        onPromptsUpdated?.();
      }
    } catch (err) {
      console.error('Error saving account default:', err);
      setError(err.response?.data?.message || 'Failed to save account default');
    } finally {
      setSavingAccountDefault(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this prompt to default? This will remove your customization.')) {
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const prompts = { [selectedPromptKey]: null };
      
      if (activeTab === 'graph') {
        const result = await apiService.updateGraphPrompts(graphId, prompts);
        if (result.success) {
          const newGraphPrompts = { ...graphPrompts };
          delete newGraphPrompts[selectedPromptKey];
          setGraphPrompts(newGraphPrompts);
          setEditorContent(defaultPrompts[selectedPromptKey] || '');
          setSuccess('Prompt reset to default');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      } else {
        if (!selectedDocument) {
          setError('Please select a document');
          return;
        }
        const result = await apiService.updateDocumentPrompts(selectedDocument, prompts);
        if (result.success) {
          const newDocPrompts = { ...documentPrompts };
          delete newDocPrompts[selectedPromptKey];
          setDocumentPrompts(newDocPrompts);
          setEditorContent(graphPrompts[selectedPromptKey] || defaultPrompts[selectedPromptKey] || '');
          setSuccess('Document prompt reset');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      }
    } catch (err) {
      console.error('Error resetting prompt:', err);
      setError(err.response?.data?.message || 'Failed to reset prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL prompts to defaults? This will remove all your customizations.')) {
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (activeTab === 'graph') {
        const result = await apiService.resetGraphPrompts(graphId);
        if (result.success) {
          setGraphPrompts({});
          setEditorContent(defaultPrompts[selectedPromptKey] || '');
          setSuccess('All graph prompts reset to defaults');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      } else {
        if (!selectedDocument) {
          setError('Please select a document');
          return;
        }
        const result = await apiService.resetDocumentPrompts(selectedDocument);
        if (result.success) {
          setDocumentPrompts({});
          setEditorContent(graphPrompts[selectedPromptKey] || defaultPrompts[selectedPromptKey] || '');
          setSuccess('Document prompts reset');
          setHasChanges(false);
          onPromptsUpdated?.();
        }
      }
    } catch (err) {
      console.error('Error resetting all prompts:', err);
      setError(err.response?.data?.message || 'Failed to reset prompts');
    } finally {
      setSaving(false);
    }
  };

  const renderDiff = () => {
    const defaultContent = defaultPrompts[selectedPromptKey] || '';
    const currentContent = editorContent;
    
    return (
      <div className="prompt-diff">
        <div className="diff-header">
          <span className="diff-label diff-removed">Default</span>
          <span className="diff-label diff-added">Current</span>
        </div>
        <div className="diff-content">
          <div className="diff-column">
            <pre>{defaultContent}</pre>
          </div>
          <div className="diff-column">
            <pre>{currentContent}</pre>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="graph-prompts-modal-overlay" onClick={onClose}>
      <div className="graph-prompts-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Graph Prompts</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {/* Tab Navigation */}
        <div className="prompt-tabs">
          <button 
            className={`tab-button ${activeTab === 'graph' ? 'active' : ''}`}
            onClick={() => setActiveTab('graph')}
          >
            Graph Defaults
            {customizedKeys.length > 0 && (
              <span className="tab-badge blue">{customizedKeys.length}</span>
            )}
          </button>
          <button 
            className={`tab-button ${activeTab === 'document' ? 'active' : ''}`}
            onClick={() => setActiveTab('document')}
          >
            Document Overrides
            {documentCustomizedKeys.length > 0 && (
              <span className="tab-badge orange">{documentCustomizedKeys.length}</span>
            )}
          </button>
        </div>

        {/* Document Selector (only for document tab) */}
        {activeTab === 'document' && (
          <div className="document-selector">
            <label>Select Document:</label>
            <select 
              value={selectedDocument || ''} 
              onChange={e => setSelectedDocument(e.target.value || null)}
            >
              <option value="">-- Select a document --</option>
              {documents.map(doc => (
                <option key={doc.document_id} value={doc.document_id}>
                  {doc.filename}
                  {doc.custom_prompts ? ' (customized)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Prompt Selector with optgroup */}
        <div className="prompt-selector">
          <label>Select Prompt:</label>
          <select 
            value={selectedPromptKey} 
            onChange={e => setSelectedPromptKey(e.target.value)}
          >
            {PROMPT_GROUPS.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.keys.map(key => {
                  const isCustomized = activeTab === 'graph'
                    ? customizedKeys.includes(key)
                    : documentCustomizedKeys.includes(key);
                  return (
                    <option key={key} value={key}>
                      {PROMPT_DISPLAY_NAMES[key] || key} {isCustomized ? '\u25CF' : '\u25CB'}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
          <span className="prompt-legend">{'\u25CF'} = Customized, {'\u25CB'} = Using Default</span>
        </div>

        {/* Prompt Description */}
        <div className="prompt-description">
          {PROMPT_DESCRIPTIONS[selectedPromptKey]}
        </div>

        {/* Status Messages */}
        {error && <div className="prompt-error">{error}</div>}
        {success && <div className="prompt-success">{success}</div>}

        {/* Loading State */}
        {loading ? (
          <div className="prompt-loading">Loading prompts...</div>
        ) : (
          <>
            {/* Editor */}
            <div className="prompt-editor-container">
              <textarea
                className="prompt-editor"
                value={editorContent}
                onChange={handleEditorChange}
                placeholder="Enter prompt content..."
                disabled={activeTab === 'document' && !selectedDocument}
              />
            </div>

            {/* Toggle Buttons */}
            <div className="prompt-toggles">
              <button 
                className={`toggle-button ${showDefault ? 'active' : ''}`}
                onClick={() => { setShowDefault(!showDefault); setShowDiff(false); }}
              >
                {showDefault ? '\u25B2 Hide Default' : '\u25BC Show Default'}
              </button>
              <button 
                className={`toggle-button ${showDiff ? 'active' : ''}`}
                onClick={() => { setShowDiff(!showDiff); setShowDefault(false); }}
              >
                {showDiff ? '\u25B2 Hide Diff' : '\u25BC Show Diff'}
              </button>
            </div>

            {/* Default View */}
            {showDefault && (
              <div className="prompt-default-view">
                <h4>Default Prompt:</h4>
                <pre>{defaultPrompts[selectedPromptKey] || 'No default available'}</pre>
              </div>
            )}

            {/* Diff View */}
            {showDiff && renderDiff()}
          </>
        )}

        {/* Action Buttons */}
        <div className="prompt-actions">
          <div className="action-group-left">
            <button 
              className="action-button reset"
              onClick={handleReset}
              disabled={saving || loading || !isCurrentPromptCustomized()}
              title="Reset this prompt to default"
            >
              Reset This Prompt
            </button>
            <button 
              className="action-button reset-all"
              onClick={handleResetAll}
              disabled={saving || loading || (activeTab === 'graph' ? customizedKeys.length === 0 : documentCustomizedKeys.length === 0)}
              title="Reset all prompts to defaults"
            >
              Reset All
            </button>
          </div>
          <div className="action-group-right">
            <button
              className="action-button account-default"
              onClick={handleSaveAsAccountDefault}
              disabled={savingAccountDefault || saving || loading}
              title="Save this prompt as your account default for all future graphs"
            >
              {savingAccountDefault ? 'Saving...' : 'Save as Account Default'}
            </button>
            <button 
              className="action-button cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              className="action-button save"
              onClick={handleSave}
              disabled={saving || loading || !hasChanges || (activeTab === 'document' && !selectedDocument)}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphPromptsModal;
